/* eslint-disable unused-imports/no-unused-vars */
import { createSelector } from 'reselect';
import { productAvailabilityEnum } from '../utils/segment';
import { sortIdToTrackingValue } from '../utils/sort';
import { selectPathToSelectedCategory } from './categoriesSelectors';
import {
  selectIsProductInStock,
  selectProduct,
  selectProductIds,
  selectVariant,
} from './productsSelectors';

function getPositionLocation(path: Array<[string | null, string]>, trackDisplayResults: boolean) {
  if (trackDisplayResults) return 'Search Result';
  const isAllCategorySelected = path && path[0] && path[0].toLowerCase() === 'all';
  return isAllCategorySelected ? 'Main Page' : path.join(', ');
}

function getProductPosition(
  productIds: { inStock: string[]; sponsored: string[]; oos: string[] },
  productId: string,
) {
  const displayOrderIds = [...productIds.inStock, ...productIds.sponsored, ...productIds.oos];
  return displayOrderIds.indexOf(productId);
}

export const selectTrackingData = createSelector(
  (state, props) => state.userState,
  (state, props) => state.campaignState.campaignId,
  (state, props) => state.campaignState.campaignName,
  (state, props) => state.searchState.searchResults,
  (state, props, trackDisplayResults) => trackDisplayResults,
  (state, props) => state.sortState.selectedSort,
  (state, props) => state.optimizelyState,
  selectPathToSelectedCategory,
  selectProduct,
  selectVariant,
  selectIsProductInStock,
  selectProductIds,
  (
    userState,
    campaignId,
    campaignName,
    searchResults,
    trackDisplayResults,
    selectedSort,
    _optimizelyState,
    pathToSelectedCategory,
    product,
    variant,
    isProductInStock,
    productIds,
  ) => {
    const productLegacyPostId = product.legacyPostId;
    const variantLegacyPostId = variant.legacyPostId;
    const { userId, role, subscriptionState, planCode } = userState;
    const productDisplayOrderPosition = getProductPosition(productIds, variant.parentId);
    const displayResults = trackDisplayResults
      ? {
          display_results: productIds.inStock.length,
          display_results_oos: productIds.oos.length,
        }
      : {};
    return {
      user_id: userId,
      subscription_state: subscriptionState && subscriptionState.toLowerCase(),
      role,
      plan_code: planCode,
      campaign_id: campaignId,
      campaign_name: campaignName,
      product_id: variantLegacyPostId,
      parent_product_id: variantLegacyPostId !== productLegacyPostId ? productLegacyPostId : 0,
      product_name: product.name,
      product_categories: product.categoryNames,
      merch_position_order: product.ordering,
      event_position_order: productDisplayOrderPosition + 1, // should start at 1
      retail_price: variant.price,
      sale_price: variant.salePrice,
      product_image_url: product.imageUrl,
      product_availability: isProductInStock
        ? productAvailabilityEnum.inStock
        : productAvailabilityEnum.outOfStock,
      position_location: getPositionLocation(
        pathToSelectedCategory,
        searchResults && trackDisplayResults,
      ),
      ...displayResults,
      // Technically, this makes this selector impure. Generally we should avoid
      // making selectors impure, since selectors use memoization. Here it's ok
      // because a user can't switch device type in the middle of a session.
      sort_name: sortIdToTrackingValue[selectedSort],
    };
  },
);

export const selectPageTrackingFullDat = createSelector(
  (state, props) => state.userState,
  (state, props) => state.campaignState.campaignId,
  (state, props) => state.campaignState.campaignName,
  (state, props) => state.sortState.selectedSort,
  (userState, campaignId, campaignName, selectedSort) => {
    const { userId, role, subscriptionState, planCode } = userState;
    return {
      user_id: userId,
      subscription_state: subscriptionState && subscriptionState.toLowerCase(),
      role,
      plan_code: planCode,
      campaign_id: campaignId,
      campaign_name: campaignName,
      sort_name: sortIdToTrackingValue[selectedSort],
    };
  },
);

export const selectPageTrackingData = createSelector(
  (state, props) => state.userState,
  (state, props) => state.sortState.selectedSort,
  (userState, selectedSort) => {
    const { userId, role, subscriptionState, planCode } = userState;

    return {
      user_id: userId,
      sort_name: sortIdToTrackingValue[selectedSort],
      //this data is not available on non-auth pages
      ...(role && { role }),
      ...(planCode && { plan_code: planCode }),
      ...(subscriptionState && {
        subscription_state: subscriptionState.toLowerCase(),
      }),
    };
  },
);
